import React from 'react';
import Header from '../../components/ui/Header';
import Seo from '../../components/Seo';

const PrivacyPage = () => {
  return (
    <>
      <Seo
        title="Privacy Policy | Neevii"
        description="Neevii Privacy Policy - How we collect, use, and protect your data."
      />
      <div className="min-h-screen">
        <Header />
        <main className="pt-24 pb-16 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-invert">
            <h1 className="text-4xl font-bold text-foreground mb-8">Privacy Policy</h1>
            <p className="text-muted-foreground mb-4">Last updated: 16 May 2026</p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                Neevii ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered accounting service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">2. Information We Collect</h2>
              <p className="text-muted-foreground mb-4">We collect information you provide directly:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Account information (name, email, company details)</li>
                <li>Financial data you upload for processing</li>
                <li>Usage data and analytics</li>
                <li>Communications with our support team</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide and improve our AI accounting services</li>
                <li>Process your financial data securely</li>
                <li>Send service updates and communications</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">4. Google Sheets Sidebar Data Processing</h2>
              <p className="text-muted-foreground mb-4">
                If you use the Neevii AI Assistant Google Sheets sidebar, we process
                receipt and transaction content that you submit to deliver extraction,
                categorization, and spreadsheet mapping features.
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Google account identity data is used for sign-in and account linking.</li>
                <li>Sidebar content is processed to generate structured bookkeeping fields.</li>
                <li>
                  AI processing may use Google Gemini APIs to classify and enrich data
                  according to your selected workflow.
                </li>
                <li>We do not request or retain data beyond what is required for service delivery and support.</li>
              </ul>
            </section>

            <section id="google-workspace-data" className="mb-8 scroll-mt-24">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                5. Google API Services — Limited Use compliance
              </h2>
              <p className="text-muted-foreground mb-4 rounded-lg border border-border/80 bg-muted/30 p-4 leading-relaxed">
                The use and transfer of information received from Google APIs to any other app will adhere to the{' '}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google API Services User Data Policy
                </a>
                , including the{' '}
                <a
                  href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
                  className="text-accent hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Limited Use requirements
                </a>
                .
              </p>
              <p className="text-muted-foreground mb-4">
                This statement applies to data received from Google Workspace APIs (for example Google Sheets) when you use Neevii products that connect to Google. We do not use such data for serving ads, and we do not sell Google user data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                6. Sharing, transfer, and disclosure of Google user data
              </h2>
              <p className="text-muted-foreground mb-4">
                When you use Neevii features that read or write Google Sheets (or other Google APIs we request with your consent), content and metadata from your Google account may be processed by Neevii and, where necessary to provide the service, transferred to the following categories of recipients:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <strong className="text-foreground">Neevii (Neevii Limited)</strong> — Our staff and systems process your data to deliver the product, provide support, and meet legal obligations.
                </li>
                <li>
                  <strong className="text-foreground">Google</strong> — Google hosts your spreadsheet and provides Google Sign-In; some workflows may use Google AI services (for example classification or enrichment) when you opt into those features, as described in section 4.
                </li>
                <li>
                  <strong className="text-foreground">Supabase</strong> — Authentication and database hosting for account linking, session data, and stored outputs you choose to save in Neevii.
                </li>
                <li>
                  <strong className="text-foreground">Railway (and similar infrastructure providers)</strong> — Application hosting for Neevii APIs that process requests from the Sheets add-on and related clients.
                </li>
                <li>
                  <strong className="text-foreground">Amazon SES</strong> — Transactional email (for example reports or notifications you request).
                </li>
                <li>
                  <strong className="text-foreground">Stripe</strong> — Payment processing if you subscribe to paid Neevii plans (billing metadata only; we do not use Google user data for ads).
                </li>
                <li>
                  <strong className="text-foreground">Authorities</strong> — We may disclose information when required by applicable law or a valid legal process.
                </li>
              </ul>
              <p className="text-muted-foreground mt-4">
                We only share Google user data with these parties as needed to operate the features you use, under appropriate contracts and security measures. If we add or change subprocessors in a way that materially affects Google user data, we will update this policy accordingly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">7. Data Security</h2>
              <p className="text-muted-foreground mb-4">
                We implement industry-standard security measures including AES-256 encryption, secure data centres, and regular security audits. We are Cyber Essentials certified, demonstrating our commitment to cybersecurity best practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">8. Your Rights</h2>
              <p className="text-muted-foreground mb-4">Under GDPR, you have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access your personal data</li>
                <li>Rectify inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">9. Contact Us</h2>
              <p className="text-muted-foreground">
                For privacy-related inquiries, contact us at{' '}
                <a href="mailto:privacy@neevii.com" className="text-accent hover:underline">privacy@neevii.com</a>
              </p>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default PrivacyPage;

